/* eslint-disable max-len */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import {
    LoadingSpinner, DataTable, number, columnTypes, percent,
    dateTranslate, SprayChart, VideoButton, COUNTS, pitchTypeOrSubType,
} from 'yankees-react-utils';
import { useOktaQuery, isLhb, Advance, PlayerReport, usePitchVideoFn, useMultiVideoFn } from '../../../data';
import ReportCard, { StyledReportCard } from '../../../components/ReportCard';
import InfieldPositioningBatterNote from './InfieldPositioningBatterNote';
import AtBatFilters from '../../../components/AtBatFilters';
import useConfig from '../../../utils/useConfig';
import usePlayerSelector from '../../../utils/usePlayerSelector';
import { VideoPlayerModal } from '../../../components/VideoPlayer';
import StatTooltip from '../../../components/StatTooltip';

const Container = styled.div`
    width: 95%;
    margin: 20px auto;
    > * {
        margin-bottom: 20px;
    }
    ${StyledReportCard} .card-body > h4 {
        font-size: 18px;
        font-weight: bold;
        text-align: left;
        text-transform: uppercase;
    }
    .table {
        font-size: 12px;
    }
`;
const H4Centered = styled.h4`
    text-align: center;
`;
const DivCentered = styled.div`
    text-align: center;
`;
const RowCentered = styled(Row)`
    text-align: center;
`;
const ColLeft = styled(Col)`
    text-align: right;
`;
const ColRight = styled(Col)`
    text-align: left;
`;
const ShiftNum = styled.div`
    font-size: 150%;
    position: relative;
    text-align: center;
    top: -36%;
    left: 52px;
`;
const { NUMBER } = columnTypes;

const ShiftTypesArray = [101, 100, 102, 108, 109, 112];
// eslint-disable-next-line max-len
const buildInfielderSeries = (translate, dfpoData, batterData) => dfpoData.infield.filter((x) => x.batterid === batterData?.PlayerID && ShiftTypesArray.indexOf(x.type) >= 0).sort((a, b) => ShiftTypesArray.indexOf(a.type) - ShiftTypesArray.indexOf(b.type)).map((infield) => ({
    shifttype: infield.type,
    shiftnum: infield.shiftnum,
    series: [
        {
            name: translate('baseball.1b'),
            data: [
                {
                    x: infield.adjx1b ? infield.adjx1b : infield.posx1b,
                    y: infield.adjy1b ? infield.adjy1b : infield.posy1b,
                },
            ],
            color: '#FF0000',
            marker: {
                lineColor: '#FF0000',
                states: {
                    hover: {
                        lineColor: '#FF0000',
                        fillColor: '#FF0000',
                    },
                },
            },
        },
        {
            name: translate('baseball.2b'),
            data: [
                {
                    x: infield.adjx2b ? infield.adjx2b : infield.posx2b,
                    y: infield.adjy2b ? infield.adjy2b : infield.posy2b,
                },
            ],
            color: '#FF0000',
            marker: {
                lineColor: '#FF0000',
                states: {
                    hover: {
                        lineColor: '#FF0000',
                        fillColor: '#FF0000',
                    },
                },
            },
        },
        {
            name: translate('baseball.3b'),
            data: [
                {
                    x: infield.adjx3b ? infield.adjx3b : infield.posx3b,
                    y: infield.adjy3b ? infield.adjy3b : infield.posy3b,
                },
            ],
            color: '#FF0000',
            marker: {
                lineColor: '#FF0000',
                states: {
                    hover: {
                        lineColor: '#FF0000',
                        fillColor: '#FF0000',
                    },
                },
            },
        },
        {
            name: translate('baseball.positions.abbr-shortstop'),
            data: [
                {
                    x: infield.adjxss ? infield.adjxss : infield.posxss,
                    y: infield.adjyss ? infield.adjyss : infield.posyss,
                },
            ],
            color: '#FF0000',
            marker: {
                lineColor: '#FF0000',
                states: {
                    hover: {
                        lineColor: '#FF0000',
                        fillColor: '#FF0000',
                    },
                },
            },
        },
    ],
}));
const getBIP = (series) => {
    if (!series || !series.length) return 0;
    let count = 0;
    series.forEach((rec) => {
        count += rec.data.length;
    });
    return count;
};
const buildPullObject = (pullData) => ({
    fbl: pullData.fbl_pull,
    nonfbl: pullData.nonfbl_pull,
    ha: pullData.gbahead_pull,
    ev: pullData.gbeven_pull,
    twok: pullData.gb2k_pull,
});
const buildBatterPullObject = (pullArray, batterData) => {
    const pullData = pullArray.filter((row) => row.batterid === batterData?.PlayerID);
    if (pullData.length === 0) return null;
    return buildPullObject(pullData[0]);
};
const buildPitcherPullObject = (pullArray, isLHB) => {
    // eslint-disable-next-line eqeqeq
    const pullData = pullArray.filter((row) => row.islhb == isLHB);
    if (pullData.length === 0) return null;
    return buildPullObject(pullData[0]);
};
const buildBuntRows = (dfpoData, batterid) => {
    const buntstats = dfpoData.buntstats.find((x) => x.batterid === batterid);
    const hometofirst = dfpoData.hometofirst.find((x) => x.playerid === batterid);
    const rungrades = dfpoData.rungrades.find((x) => x.playerid === batterid);
    const gbfb = dfpoData.gbfb.find((x) => x.playerid === batterid);
    const stealstats = dfpoData.stealstats.find((x) => x.playerid === batterid);
    return [{ ...buntstats, ...hometofirst, ...rungrades, ...gbfb, ...stealstats }];
};
const buildSprayChartSeries = (translate, plays) => {
    let series;
    if (!plays || !plays.length) return null;

    const hits = { data: [],
        name: translate('baseball.hits_other'),
        index: 1,
        id: 'hit',
        marker: { symbol: 'circle', radius: 4, lineColor: '#0000ff', fillColor: '#ffffff', lineWidth: 1 } };
    const battedouts = { data: [],
        name: translate('baseball.outs_other'),
        index: 2,
        id: 'out',
        marker: { symbol: 'diamond', radius: 4, lineColor: '#0000ff' } };
    const roe = { data: [],
        name: translate('baseball.abbr-reach-error'),
        index: 3,
        id: 'roe',
        marker: { symbol: 'triangle', radius: 4, lineColor: '#0000ff', fillColor: '#ffffff', lineWidth: 1 } };
    const hr = { data: [],
        name: translate('baseball.home-run'),
        index: 4,
        id: 'hr',
        marker: { symbol: 'square', radius: 4, lineColor: '#0000ff', fillColor: '#ffffff', lineWidth: 1 } };

    for (let i = 0; i < plays.length; ++i) {
        if (plays[i].platex && plays[i].platez) {
            // resultid
            plays[i].outsbefore = plays[i].outs;// need this value for the modal display
            plays[i].batter = `${plays[i].batterusesname} ${plays[i].batterlastname}`;
            plays[i].pitcher = `${plays[i].pitcherusesname} ${plays[i].pitcherlastname}`;
            const point = {
                x: plays[i].x,
                y: plays[i].y,
                name: '',
                tooltip:
                    `${''
                    + `${translate('common.date')}: `}${dateTranslate(translate, plays[i].gamedate)}<br />`
                    + `${translate('sections.opponent.player.pitchtype')}: ${pitchTypeOrSubType(plays[i].fxsubtype || plays[i].pitchtype, undefined, undefined, translate)}<br />${
                        plays[i].velocity ? `${translate('baseball.pitch-velo')}: ${translate('format.abbr-mph', { value: number(plays[i].velocity, 1) })}<br/>` : ''
                    }${plays[i].istop ? `${translate('baseball.abbr-top')} ` : `${translate('baseball.abbr-bottom')} `}${plays[i].inning}, ${
                        plays[i].outs} ${translate('baseball.outs', { count: plays[i].outs })}, ${
                        plays[i].balls}-${plays[i].strikes
                    }<br />${translate('baseball.pitcher_one')}: ${plays[i].pitcherusesname} ${plays[i].pitcherlastname}
                    <br />${translate('baseball.batter')}: ${plays[i].batterusesname} ${plays[i].batterlastname} ${plays[i].islastpitch
    ? `<br />${plays[i].hitvelocity ? `${translate('baseball.ev')}: ${translate('format.abbr-mph', { value: number(plays[i].hitvelocity, 1) })}` : ''}`
    : ''}`,
                url: plays[i].url,
                gameid: plays[i].gameid,
                eventseq: plays[i].eventseq,
                pitchseq: plays[i].pitchseq,
            };
            switch (plays[i].outcome) {
            case 'Hit':
                hits.data.push(point); break;
            case 'Out':
                battedouts.data.push(point); break;
            case 'RoE':
                roe.data.push(point); break;
            case 'HR':
                hr.data.push(point); break;
            default: break;
            }
        }
    }

    series = [hits, battedouts, roe, hr];
    series = series.filter((x) => x.data.length > 0);
    return series;
};

const isInfieldPositioningHit = (x) => x.hittypecode === 'G'
    || x.hittypecode === 'B'
    || (x.distance < 150 && x.hittypecode !== 'P');

const InfieldPositioning = ({ pitcher, batter, opposingTeamID }) => {
    const { t: translate } = useTranslation();
    const { CurrentYear, UsePreviousYearData } = useConfig();
    const [visibleBvPScatterData, setVisibleBvPScatterData] = useState([]);
    const [visiblePvBScatterData, setVisiblePvBScatterData] = useState([]);
    const videoPlayerModalContainer = useRef();
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoPlaylist, setVideoPlaylist] = useState(false);
    const { TeamID } = usePlayerSelector();
    const pitchVideoFn = usePitchVideoFn();
    const multiVideoFn = useMultiVideoFn();

    const ShiftTypes = {
        101: `Reg / ${translate('sections.opponent.player.abbr-first-pitch')}`,
        100: translate('sections.opponent.player.hitter-ahead'),
        102: `2 ${translate('baseball.strikes_other')}`,
        108: `R1, ${translate('sections.opponent.player.dp-situation')}`,
        109: translate('baseball.abbr-runners-in-position'),
        112: `R1, 2 ${translate('baseball.outs_other')}`,
    };

    // eslint-disable-next-line no-unused-vars
    const { authState: { accessToken } } = useOktaAuth();

    const filterRefreshDefaults = {
        counter: 0,
        vsteam: { name: translate('common.all'), value: -1 },
        catcher: { name: translate('common.all'), value: -1 },
        dateStart: new Date(new Date().getFullYear() - 1, 0, 1),
        dateEnd: new Date(),
    };
    const filterDefaults = {
        baserunners: -1,
        counts: [-1],
        pitchtype: [-1],
        pitchresult: [-1],
    };

    // eslint-disable-next-line no-unused-vars
    const [filtersRefresh, setFiltersRefresh] = useState(filterRefreshDefaults);
    const [filters, setFilters] = useState(filterDefaults);

    const filterVisibleData = () => {
        // eslint-disable-next-line no-use-before-define
        if (sprayChartPvBData && sprayChartPvBData.bip && sprayChartBvPData && sprayChartBvPData.bip) {
            // apply filters and save result to visibility state
            // eslint-disable-next-line no-use-before-define
            setVisiblePvBScatterData(buildSprayChartSeries(translate, applyFilters(sprayChartPvBData.bip)));
            // eslint-disable-next-line no-use-before-define
            setVisibleBvPScatterData(buildSprayChartSeries(translate, applyFilters(sprayChartBvPData.bip)));
        }
    };
    const applyFilters = (data) => {
        let filteredData = data.filter(isInfieldPositioningHit);
        if (filters.counts.includes(-2)) {
            // Attack
            filteredData = filteredData.filter((value) => (value.strikes === 0 && value.balls === 0)
                || (value.strikes === 0 && value.balls === 1)
                || (value.strikes === 1 && value.balls === 1)
                || (value.strikes === 1 && value.balls === 0)
                || (value.strikes === 0 && value.balls === 2)
                || (value.strikes === 1 && value.balls === 2)
                || (value.strikes === 0 && value.balls === 3)
                || (value.strikes === 1 && value.balls === 3));
        }
        if (filters.counts.includes(-3)) {
            // 2K
            filteredData = filteredData.filter((value) => value.strikes === 2);
        }
        if (filters.counts.filter((x) => x >= 0).length > 0) {
            const fdNew = [];
            filteredData.forEach((p) => {
                const tgtCountId = COUNTS.find((y) => y.balls === p.balls && y.strikes === p.strikes)?.id;
                if (filters.counts.includes(tgtCountId)) {
                    fdNew.push(p);
                }
            });
            filteredData = fdNew;
        }
        if (!filters.pitchtype.includes(-1)) {
            filteredData = filteredData.filter((p) => (filters.pitchtype.includes(-1)
                || (filters.pitchtype.includes(1) && [9, 10].includes(p.pitchtype))
                || (filters.pitchtype.includes(0) && ![9, 10].includes(p.pitchtype))
                || filters.pitchtype.includes(p.fxsubtype != null && p.fxsubtype > 0 ? (p.pitchtype * 1000) + p.fxsubtype : p.pitchtype)
                // eslint-disable-next-line max-len
                || (!(p.pitchtype === 3 && (p.fxsubtype === 34 || p.fxsubtype === 33)) && filters.pitchtype.filter((x) => x < 1000 && p.fxsubtype != null && p.pitchtype === x).length > 0)));
        }
        return filteredData;
    };

    const isLHP = pitcher?.Throws === 2;
    const isLHB = isLhb(pitcher, batter);

    const dfpoParams = { pitcherId: pitcher?.PlayerID, battingTeamId: opposingTeamID, pitchingTeamId: TeamID };
    if (!window.$IsDugoutApp) dfpoParams.batterId = batter?.PlayerID;
    const dfpoQuery = useOktaQuery(['dfpo', { ...dfpoParams }], (t) => Advance.dfpo(t, dfpoParams), { enabled: opposingTeamID != null && TeamID != null && pitcher?.PlayerID != null && batter?.PlayerID != null });
    const { data: dfpoData, isFetching: isDfpoLoading } = dfpoQuery;

    const sprayChartBvPQuery = useOktaQuery(['spraychartbvp', { batterId: batter?.PlayerID, islhp: isLHP }], (t) => PlayerReport.spraychart(t, { batterId: batter?.PlayerID, startdate: `${CurrentYear - (UsePreviousYearData ? 1 : 0)}-01-01`, enddate: `${CurrentYear}-12-31`, islhp: isLHP }), { enabled: batter?.PlayerID != null && isLHP != null });
    const { data: sprayChartBvPData, isFetching: isSprayChartBvPLoading } = sprayChartBvPQuery;

    const sprayChartPvBQuery = useOktaQuery(['spraychartpvb', { pitcherId: pitcher?.PlayerID, islhb: isLHB }], (t) => PlayerReport.spraychart(t, { pitcherId: pitcher?.PlayerID, startdate: `${CurrentYear - (UsePreviousYearData ? 1 : 0)}-01-01`, enddate: `${CurrentYear}-12-31`, islhb: isLHB }), { enabled: pitcher?.PlayerID != null && isLHB != null });
    const { data: sprayChartPvBData, isLoading: isSprayChartPvBLoading } = sprayChartPvBQuery;

    useEffect(() => {
        setFiltersRefresh(filterRefreshDefaults);
        filterVisibleData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batter?.PlayerID, pitcher?.PlayerID, filters, sprayChartBvPData, sprayChartPvBData]);

    const availablePitchTypes = useMemo(() => new Set((sprayChartBvPData?.bip ?? []).concat(sprayChartPvBData?.bip ?? [])
        .filter(isInfieldPositioningHit)
        .map((p) => (p.fxsubtype != null && p.fxsubtype !== 0 ? (p.pitchtype * 1000) + p.fxsubtype : p.pitchtype))),
    [sprayChartBvPData, sprayChartPvBData]);

    if (isDfpoLoading || !pitcher || !batter || isSprayChartBvPLoading || isSprayChartPvBLoading) {
        return <div style={{ margin: '10px' }}><LoadingSpinner /></div>;
    }

    const infielderSeries = buildInfielderSeries(translate, dfpoData, batter);
    const batterPullObject = buildBatterPullObject(dfpoData.pullpercent, batter);
    const pitcherPullObject = buildPitcherPullObject(dfpoData.pitcherpull, isLHB);
    const buntRows = buildBuntRows(dfpoData, batter?.PlayerID);

    const playAllPvBVideo = async () => {
        if (window.$MatchupEmbeddedVideoDisabled) return;
        setShowVideoModal(true);
        const playlist = await multiVideoFn(applyFilters(sprayChartPvBData.bip));
        setVideoPlaylist(playlist);
    };

    const playAllBvPVideo = async () => {
        if (window.$MatchupEmbeddedVideoDisabled) return;
        setShowVideoModal(true);
        const playlist = await multiVideoFn(applyFilters(sprayChartBvPData.bip));
        setVideoPlaylist(playlist);
    };

    const superheader = (
        <tr>
            <td style={{ backgroundColor: '#005A9C', color: '#FFF', borderRight: '1px solid #FFF', textAlign: 'center' }} colSpan={5}>&nbsp;</td>
            <td style={{ backgroundColor: '#005A9C', color: '#FFF', borderLeft: '1px solid #FFF', borderRight: '1px solid #FFF', textAlign: 'center' }} colSpan={6}>{translate('sections.opponent.player.bunt-attempts')}</td>
            <td style={{ backgroundColor: '#005A9C', color: '#FFF', borderLeft: '1px solid #FFF', textAlign: 'center' }} colSpan={2}>{translate('sections.opponent.player.bunt-hits')}</td>
        </tr>
    );

    const buntTableColumns = [
        { id: 'prs', title: translate('sections.opponent.player.abbr-run-speed'), statistic: 'RunSpd', type: NUMBER },
        { id: 'hto1b', title: `${translate('sections.opponent.player.abbr-home-gtr-1b')}`, statistic: 'HomeToFirstTime', type: NUMBER, precision: 2 },
        { id: 'sb2b', title: `${translate('baseball.abbr-stolen-base')} ${translate('baseball.2b')}`, statistic: 'SB2BRatio', transform: (row) => (`${row.sb2b}/${row.cs2b + row.sb2b}`), type: columnTypes.TEXT, headerClass: 'text-center', cellClass: 'text-center' },
        { id: 'sb3b', title: `${translate('baseball.abbr-stolen-base')} ${translate('baseball.3b')}`, statistic: 'SB3BRatio', transform: (row) => (`${row.sb3b}/${row.cs3b + row.sb3b}`), type: columnTypes.TEXT, headerClass: 'text-center', cellClass: 'text-center' },
        { id: 'gbfb', title: `${translate('baseball.abbr-gb-fb')} ${translate('baseball.abbr-versus')} ${isLHP ? translate('baseball.lhp') : translate('baseball.rhp')}`, transform: (row) => (`${isLHP ? (row.lhpgb / row.lhpfb) : (row.rhpgb / row.rhpfb)}`), type: NUMBER, precision: 2 },
        { id: 'firstp', title: translate('sections.opponent.player.abbr-first-pitch'), type: NUMBER },
        { id: 'inplay', title: translate('sections.opponent.player.in-play'), type: NUMBER },
        { id: 'early', title: translate('sections.opponent.player.early'), type: NUMBER },
        { id: 'total', title: translate('common.total'), type: NUMBER },
        { id: 'htrahd', title: translate('sections.opponent.player.abbr-hitter-ahead'), type: NUMBER },
        { id: 'twostrikes', title: '2k', type: NUMBER },
        { id: 'bunthits1B', title: translate('baseball.1b'), type: NUMBER },
        { id: 'bunthits3B', title: translate('baseball.3b'), type: NUMBER },
    ];

    return (
        <Container>
            <div ref={videoPlayerModalContainer} />
            <Row>
                <Col md={7} sm={12}>
                    <ReportCard>
                        <Row>
                            {infielderSeries && infielderSeries.map((shift) => (
                                <Col sm={4} key={ShiftTypes[shift.shifttype]}>
                                    <H4Centered>{ShiftTypes[shift.shifttype]}</H4Centered>
                                    <SprayChart type="infieldbw" enableLegend={false} seriesData={shift.series} />
                                    <ShiftNum>{shift.shiftnum}</ShiftNum>
                                </Col>
                            ))}
                        </Row>
                    </ReportCard>
                </Col>
                <Col md={5} sm={12}>
                    <InfieldPositioningBatterNote playerId={batter?.PlayerID} />
                </Col>
            </Row>
            <Row>
                <ReportCard>
                    <DataTable
                        columns={buntTableColumns}
                        rows={buntRows}
                        superheader={superheader}
                        statTooltip={StatTooltip}
                    />
                </ReportCard>
            </Row>
            <Row>
                <ReportCard>
                    <Row>
                        <Col sm={4}>
                            <AtBatFilters
                                filters={filters}
                                pitchTypeFilter={(value) => setFilters({ ...filters, pitchtype: value })}
                                availablePitchTypes={availablePitchTypes}
                            />
                        </Col>
                        <Col sm={6}>
                            <AtBatFilters
                                filters={filters}
                                countsFilter={(value) => setFilters({ ...filters, counts: value })}
                            />
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        <Col sm={12} md={6}>
                            <RowCentered>
                                <h3>{batter?.FullName} {translate('baseball.abbr-versus')} {isLHP ? translate('baseball.lhp') : translate('baseball.rhp')}</h3>
                            </RowCentered>
                            <SprayChart
                                type="infield"
                                enableLegend={false}
                                seriesData={visibleBvPScatterData}
                                videoPlayerModal={VideoPlayerModal}
                                pitchVideoFn={pitchVideoFn}
                            />
                            <DivCentered>
                                {getBIP(visibleBvPScatterData)} {translate('sections.opponent.player.balls-in-play')}
                                &nbsp;{!window.$IsDugoutApp && applyFilters(sprayChartBvPData.bip).length > 0 && (<VideoButton icon={translate('common.all')} onClick={playAllBvPVideo} />)}
                            </DivCentered>
                            {batterPullObject && (
                                <div>
                                    <RowCentered>
                                        <b>{translate('sections.opponent.player.gb-pull-pct')}</b>
                                    </RowCentered>
                                    <RowCentered>
                                        <ColLeft><b>Fbl: </b>{percent(batterPullObject.fbl)}</ColLeft>
                                        <ColRight><b>EV: </b>{percent(batterPullObject.ev)}</ColRight>
                                    </RowCentered>
                                    <RowCentered>
                                        <ColLeft><b>nFbl: </b>{percent(batterPullObject.nonfbl)}</ColLeft>
                                        <ColRight><b>2K: </b>{percent(batterPullObject.twok)}</ColRight>
                                    </RowCentered>
                                    <RowCentered>
                                        <Col><b>HA: </b>{percent(batterPullObject.ha)}</Col>
                                    </RowCentered>
                                </div>
                            )}
                        </Col>
                        <Col sm={12} md={6}>
                            <RowCentered>
                                <h3>{pitcher?.FullName} {translate('baseball.abbr-versus')} {isLHB ? translate('baseball.lhh') : translate('baseball.rhh')}</h3>
                            </RowCentered>
                            <SprayChart
                                type="infield"
                                enableLegend={false}
                                seriesData={visiblePvBScatterData}
                                videoPlayerModal={VideoPlayerModal}
                                pitchVideoFn={pitchVideoFn}
                            />
                            <DivCentered>
                                {getBIP(visiblePvBScatterData)} {translate('sections.opponent.player.balls-in-play')}
                                &nbsp;{!window.$IsDugoutApp && applyFilters(sprayChartPvBData.bip).length > 0 && (<VideoButton icon={translate('common.all')} onClick={playAllPvBVideo} />)}
                            </DivCentered>
                            {pitcherPullObject && (
                                <div>
                                    <RowCentered>
                                        <b>{translate('sections.opponent.player.gb-pull-pct')}</b>
                                    </RowCentered>
                                    <RowCentered>
                                        <ColLeft><b>Fbl: </b>{percent(pitcherPullObject.fbl)}</ColLeft>
                                        <ColRight><b>EV: </b>{percent(pitcherPullObject.ev)}</ColRight>
                                    </RowCentered>
                                    <RowCentered>
                                        <ColLeft><b>nFbl: </b>{percent(pitcherPullObject.nonfbl)}</ColLeft>
                                        <ColRight><b>2K: </b>{percent(pitcherPullObject.twok)}</ColRight>
                                    </RowCentered>
                                    <RowCentered>
                                        <Col><b>HA: </b>{percent(pitcherPullObject.ha)}</Col>
                                    </RowCentered>
                                </div>
                            )}
                        </Col>
                    </Row>
                </ReportCard>
            </Row>
            <VideoPlayerModal
                playlist={videoPlaylist}
                show={showVideoModal}
                onHide={() => { setVideoPlaylist(null); setShowVideoModal(false); }}
                container={videoPlayerModalContainer}
                hideDownload
                hideShare
                hideFrameGrab
            />
        </Container>
    );
};

InfieldPositioning.propTypes = {
    // pitcherId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    // batterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    pitcher: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
    }).isRequired,
    batter: PropTypes.shape({
        PlayerID: PropTypes.number.isRequired,
        FullName: PropTypes.shape.isRequired,
        Bats: PropTypes.number.isRequired,
        Throws: PropTypes.number.isRequired,
    }).isRequired,
    opposingTeamID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

InfieldPositioning.defaultProps = {
};

export default InfieldPositioning;
